/**
 * Lightweight toast notification system.
 * Uses a simple hook pattern — no global store needed.
 */

import React, { useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { createPortal } from 'react-dom';

import { onApiWarnings } from '@/shared/services/api';
import { Z } from '@shared/constants/zIndex';
import { usePortalContainer } from '@shared/hooks';

import { TOAST_DISMISS_MS, ToastContext, type ToastAction } from './ToastContext';

type ToastVariant = 'success' | 'warning' | 'error';

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
  action?: ToastAction;
}

/**
 * Renders a single toast notification with auto-dismiss behavior.
 *
 * Displays the toast message in a styled banner (green for success, red for error)
 * with a dismiss button. Automatically calls onDismiss after 4 seconds via a timeout
 * that is cleaned up if the component unmounts early.
 *
 * @param props - Component properties.
 * @param props.toast - The toast item containing id, message, and variant.
 * @param props.onDismiss - Callback invoked to remove this toast from the stack.
 * @returns A React element rendering the styled toast notification.
 */
function ToastNotification({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, TOAST_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  const variantStyles: Record<ToastVariant, string> = {
    success: 'bg-success-subtle border-success text-success',
    warning: 'bg-warning-subtle border-warning text-warning',
    error: 'bg-destructive-subtle border-destructive text-destructive-text',
  };
  const bgColor = variantStyles[toast.variant];

  return (
    <div
      className={`pointer-events-auto rounded-xl border px-4 py-3 text-sm shadow-lg transition-all ${bgColor}`}
      role="alert"
    >
      <div className="flex items-center justify-between gap-3">
        <span>{toast.message}</span>
        {toast.action && (
          <button
            type="button"
            onClick={() => {
              toast.action?.onClick();
              onDismiss();
            }}
            className="shrink-0 font-semibold text-current underline underline-offset-2 opacity-80 hover:opacity-100"
          >
            {toast.action.label}
          </button>
        )}
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 text-current opacity-50 hover:opacity-100"
          aria-label="Dismiss"
        >
          &times;
        </button>
      </div>
    </div>
  );
}

/**
 * Provides toast notification context to its subtree.
 *
 * Manages a stack of toast notifications rendered in a fixed position at the
 * bottom-right of the viewport via a portal. Each toast auto-dismisses after
 * 4 seconds. Supports 'success' and 'error' variants.
 *
 * @param props - Component properties.
 * @param props.children - Child elements that can access toast via the `useToast` hook.
 * @returns A React element wrapping children with toast context and the notification portal.
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  // Follows the fullscreen element so toasts still appear over a full-screen editor.
  const portalContainer = usePortalContainer();
  // Ids come from a ref, not a module-level `++counter`: the React Compiler
  // cannot lower an update to a global and skips the whole component when it
  // meets one, which left the context value below a fresh object per render.
  const nextId = useRef(0);

  const showToast = useCallback(
    (message: string, variant: ToastVariant = 'success', options?: { action?: ToastAction }) => {
      nextId.current += 1;
      const id = nextId.current;
      // Conditional spread: tsconfig has exactOptionalPropertyTypes, so
      // `action: undefined` is a type error on the optional field.
      setToasts(prev => [
        ...prev,
        { id, message, variant, ...(options?.action ? { action: options.action } : {}) },
      ]);
    },
    []
  );

  // Bridge API warning callbacks into the toast system
  const showToastRef = useRef(showToast);
  useLayoutEffect(() => {
    showToastRef.current = showToast;
  });
  useEffect(() => {
    onApiWarnings(warnings => {
      for (const w of warnings) showToastRef.current(w, 'warning');
    });
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // The value's identity is part of the contract: consumers put callbacks built
  // on it in effect deps, so a fresh object on every toast shown or dismissed
  // re-ran their loads, and a load whose failure shows a toast never stopped
  // (the cuts request, 20 times in 140ms).
  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          className="pointer-events-none fixed right-4 bottom-20 flex flex-col gap-2"
          style={{ zIndex: Z.TOAST }}
        >
          {toasts.map(toast => (
            <ToastNotification key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
          ))}
        </div>,
        portalContainer
      )}
    </ToastContext.Provider>
  );
}
